/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { Editor, InAppEditor } from 'cio/services/editor';
import { action } from '@ember/object';
import { EDITORS, InAppEditorName } from 'cio/utils/editor-utils';
import { restartableTask, task, timeout } from 'ember-concurrency';
import { inject as service } from '@ember/service';
import DS from 'ember-data';
import { tracked } from '@glimmer/tracking';
import TemplateModel, { DEFAULT_PAGE_SIZE } from 'cio/models/template';
import { isEmpty } from '@ember/utils';
import { Channel } from 'cio/models/newsletter';
import AnalyticsService from 'cio/services/analytics';
import InAppStaticTemplatesService, { InAppStaticTemplate } from 'cio/services/in-app-static-templates';
import { all } from 'rsvp';
import config from 'cio/config/environment';
import { TrackedEvents } from 'cio/utils/in-app/events';
import ComposerEditableEntity from 'cio/utils/composer/composer-editable-entity';
import EnvironmentService from 'cio/services/environment';

interface InAppChooseTemplateSignature {
  Args: {
    editableEntity: ComposerEditableEntity;
    onStartFromScratch: (editor: InAppEditor) => unknown;
    onSelect: (template: TemplateModel | InAppStaticTemplate) => Promise<void>;
  };
  Element: HTMLDivElement;
}

enum Tabs {
  USER_TEMPLATES = 'user_templates',
  STATIC_TEMPLATES = 'static_templates',
}

export default class InAppChooseTemplate extends Component<InAppChooseTemplateSignature> {
  @service store!: DS.Store;
  @service analytics!: AnalyticsService;
  @service inAppStaticTemplates!: InAppStaticTemplatesService;
  @service environment!: EnvironmentService;

  readonly itemsPerPage = DEFAULT_PAGE_SIZE;
  readonly editorName = InAppEditorName;
  readonly tabs = Tabs;

  @tracked selectedTab: Tabs = Tabs.STATIC_TEMPLATES;
  @tracked userTemplates: TemplateModel[] = [];
  @tracked staticTemplates: InAppStaticTemplate[] = [];
  @tracked previewing: TemplateModel | InAppStaticTemplate | null = null;
  @tracked searchTerm = '';
  @tracked page = 1;
  @tracked templateTotals = {
    [Editor.VISUAL]: 0,
  };

  constructor(owner: unknown, args: InAppChooseTemplateSignature['Args']) {
    super(owner, args);
    this.initializePageTask.perform();
  }

  get isPageLoading() {
    return this.initializePageTask.isRunning;
  }

  get isUserTemplatesTabDisabled() {
    return this.templateTotals[Editor.VISUAL] === 0 && this.searchTerm === '' && !this.fetchUserTemplatesTask.isRunning;
  }

  get hasUserTemplates() {
    return !isEmpty(this.userTemplates);
  }

  get hasStaticTemplates() {
    return !isEmpty(this.filteredStaticTemplates);
  }

  get filteredStaticTemplates() {
    if (isEmpty(this.searchTerm)) {
      return this.staticTemplates;
    }

    return this.staticTemplates.filter((template) =>
      template.name.toLowerCase().includes(this.searchTerm.toLowerCase()),
    );
  }

  get showPagination() {
    return this.paginationTotal > this.itemsPerPage;
  }

  get paginationTotal() {
    return this.templateTotals[Editor.VISUAL];
  }

  initializePageTask = task(async () => {
    await all([this.fetchUserTemplatesTask.perform(false), this.fetchStaticTemplatesTask.perform()]);
    this.selectedTab = this.hasUserTemplates ? Tabs.USER_TEMPLATES : Tabs.STATIC_TEMPLATES;
  });

  fetchUserTemplatesTask = restartableTask(async (debounce: boolean = true) => {
    if (debounce) {
      await timeout(config.APP.debounceDuration);
    }

    let templates = await this.store.query('template', {
      page: this.page,
      editor: Editor.VISUAL,
      search: this.searchTerm,
      filter_by_language: true,
      channel: Channel.InApp,
    });

    let {
      meta: { totals },
    } = templates as any;

    this.templateTotals = totals;
    this.userTemplates = templates.toArray();
  });

  fetchStaticTemplatesTask = task(async () => {
    this.staticTemplates = await this.inAppStaticTemplates.fetchTemplates();
  });

  onSelectTask = task(async (selected: TemplateModel | InAppStaticTemplate) => {
    await this.args.onSelect(selected);
    this.trackBuilderChosenFromTemplate(selected);
  });

  @action
  onStartFromScratch(editor: InAppEditorName) {
    let { name } = EDITORS[editor];
    let editorType = name as InAppEditor;

    this.args.onStartFromScratch(editorType);
    this.trackBuilderChosenFromScratch(editorType);
  }

  @action
  onPageChange(page: number) {
    this.page = page;
    this.fetchUserTemplatesTask.perform();
  }

  @action
  search(e: KeyboardEvent | MouseEvent) {
    e.preventDefault();
    this.searchTerm = (e?.target as HTMLFormElement).search.value;
    this.fetchUserTemplatesTask.perform();
  }

  @action
  onPreview(template: TemplateModel | InAppStaticTemplate | null) {
    this.previewing = template;
  }

  @action
  onSelectTab(tab: Tabs) {
    this.selectedTab = tab;
  }

  @action
  noop() {}

  private trackBuilderChosenFromScratch(editor: InAppEditor) {
    this.analytics.track('in_app', TrackedEvents.BuilderChosen, {
      environmentId: this.environment.current.id,
      editor,
      message: this.args.editableEntity.model,
      template: this.args.editableEntity.template,
    });
  }

  private trackBuilderChosenFromTemplate(templateToCopy: TemplateModel | InAppStaticTemplate) {
    this.analytics.track('in_app', TrackedEvents.BuilderChosen, {
      environmentId: this.environment.current.id,
      editor: Editor.VISUAL,
      message: this.args.editableEntity.model,
      template: this.args.editableEntity.template,
      templateToCopy,
    });
  }
}
